/**
 * useAppUpdateChecker — keeps long-lived tabs on the latest deploy.
 *
 * How it works
 * ─────────────
 * 1. On mount, fetch `/version.json` (force network — service worker has
 *    a hard bypass for this path).  Stash the response as the baseline
 *    version this tab is running.
 * 2. Every 60s while the tab is in the foreground (`usePageVisibility`),
 *    re-fetch `/version.json`.  If the response's `version` field differs
 *    from the baseline → a new deploy has landed.
 * 3. When the SW broadcasts `{ type: 'sw-updated' }` (a new worker has
 *    taken control), trigger a check immediately so users don't wait the
 *    full 60s.
 * 4. On `pageshow` with `event.persisted === true` (bfcache restore on
 *    iOS Safari), force a check — the page might have been frozen for
 *    days.
 * 5. When a new version is detected, reload with `window.location.reload()`
 *    — this preserves cookies + localStorage so auth survives, and the
 *    SW's network-first index.html handler guarantees the new build is
 *    served on the next request.
 *
 * Idle gating (mode D — aggressive but safe)
 * ──────────────────────────────────────────
 * Reloading mid-typing would frustrate users.  Before reloading we check:
 *   • Active element isn't an input / textarea / contenteditable
 *   • No recent user activity (mouse / touch / key / scroll) within the
 *     last `IDLE_MS`
 *   • document.visibilityState === 'visible'
 * If unsafe, we re-check every 2s until safe.  This is fast in practice
 * — a couple of seconds of stillness is the norm.
 *
 * Auth preservation
 * ─────────────────
 * `window.location.reload()` does NOT touch cookies (httpOnly auth cookie
 * intact), does NOT touch localStorage (theme prefs, draft state intact),
 * does NOT touch sessionStorage.  Service worker stays registered.  The
 * only thing that changes is the React tree gets the fresh JS bundle.
 */
import { useEffect, useRef } from 'react';
import usePageVisibility from './usePageVisibility';

const VERSION_URL = '/version.json';
const POLL_MS = 60 * 1000;       // 60s background poll
const IDLE_MS = 1500;            // last user activity must be older than this
const RETRY_MS = 2000;           // re-check idleness every 2s if currently busy

/** Returns true when we can safely reload without disrupting the user. */
function isSafeToReload(lastActivityRef) {
  if (typeof document === 'undefined') return false;
  if (document.visibilityState !== 'visible') return false;
  // Don't reload while the user is typing
  const el = document.activeElement;
  const tag = el?.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return false;
  if (el?.isContentEditable) return false;
  // Don't reload mid-scroll / mid-tap
  if (Date.now() - lastActivityRef.current < IDLE_MS) return false;
  return true;
}

async function fetchVersion() {
  try {
    const resp = await fetch(`${VERSION_URL}?_=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'omit',
    });
    if (!resp.ok) return null;
    const data = await resp.json();
    return data?.version ? String(data.version) : null;
  } catch {
    return null;
  }
}

export default function useAppUpdateChecker() {
  const baselineRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const pendingReloadRef = useRef(false);
  const visible = usePageVisibility();

  // Track latest user activity so we can detect "safe to reload" moments
  useEffect(() => {
    const bump = () => { lastActivityRef.current = Date.now(); };
    const opts = { passive: true };
    window.addEventListener('mousedown', bump, opts);
    window.addEventListener('touchstart', bump, opts);
    window.addEventListener('keydown', bump, opts);
    window.addEventListener('scroll', bump, opts);
    return () => {
      window.removeEventListener('mousedown', bump);
      window.removeEventListener('touchstart', bump);
      window.removeEventListener('keydown', bump);
      window.removeEventListener('scroll', bump);
    };
  }, []);

  const scheduleReload = () => {
    if (pendingReloadRef.current) return;
    pendingReloadRef.current = true;
    const tryReload = () => {
      if (isSafeToReload(lastActivityRef)) {
        // eslint-disable-next-line no-console
        console.info('[update] Reloading for new app version');
        window.location.reload();
        return;
      }
      setTimeout(tryReload, RETRY_MS);
    };
    tryReload();
  };

  const check = async (reason) => {
    const v = await fetchVersion();
    if (!v) return;
    if (baselineRef.current === null) {
      baselineRef.current = v;
      return;
    }
    if (v !== baselineRef.current) {
      // eslint-disable-next-line no-console
      console.info(`[update] New version detected (${reason}): ${baselineRef.current} → ${v}`);
      scheduleReload();
    }
  };

  // Initial baseline + foreground polling
  useEffect(() => {
    let cancelled = false;
    let intervalId = null;

    (async () => {
      await check('init');
      if (cancelled) return;
    })();

    if (visible) {
      intervalId = setInterval(() => check('poll'), POLL_MS);
    }

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // bfcache restore (iOS Safari) — force a check
  useEffect(() => {
    const onPageShow = (e) => {
      if (e?.persisted) check('bfcache-restore');
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Service worker "new version waiting" — broadcast trigger
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMessage = (event) => {
      if (event?.data?.type === 'sw-updated') {
        check('sw-update');
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
